import {
    MAX_TOKEN_LENGTH,
    VISITOR_TOKEN_PARAM,
    isVisitorTokenEnabled,
} from "./shared";

/**
 * Browser-side capture of the email-link token (`?aqt=<token>`).
 *
 * `captureVisitorTokenFromUrl()` reads the token, rewrites the address bar
 * without it (history.replaceState, every other param left byte for byte) and
 * keeps the token in module memory — never in cookies, localStorage,
 * sessionStorage, IndexedDB or the dataLayer. It is called from
 * `src/instrumentation-client.ts`, which Next.js runs before React hydration and
 * therefore before any consent-gated tag (GTM, GA4, the Salesforce beacon) can
 * exist, and again (idempotently) when the consent hook module loads.
 *
 * Browser only. A no-op unless NEXT_PUBLIC_VISITOR_TOKEN is on.
 */

// A real token only ever contains these characters.
const TOKEN_CHARACTERS = /^[A-Za-z0-9._-]+$/;

let pendingToken: string | null = null;

export function hasPendingVisitorToken(): boolean {
    return pendingToken !== null;
}

/** Hands the token over (once) and forgets it. */
export function takePendingVisitorToken(): string | null {
    const token = pendingToken;
    pendingToken = null;
    return token;
}

/** Puts a taken token back, e.g. when the attempt must be retried later. */
export function restorePendingVisitorToken(token: string): void {
    pendingToken = token;
}

export function discardPendingVisitorToken(): void {
    pendingToken = null;
}

function decode(component: string): string | null {
    try {
        return decodeURIComponent(component.replace(/\+/g, " "));
    } catch {
        return null;
    }
}

export function captureVisitorTokenFromUrl(): void {
    if (typeof window === "undefined" || !isVisitorTokenEnabled()) return;

    const { search, pathname, hash } = window.location;
    if (search.length <= 1) return;

    // Remove only the aqt pairs, by hand: round-tripping through URLSearchParams
    // would re-encode every other param (spaces become '+', etc.).
    const kept: string[] = [];
    let token: string | null = null;
    let found = false;
    for (const pair of search.slice(1).split("&")) {
        if (pair === "") continue;
        const separator = pair.indexOf("=");
        const key = decode(separator === -1 ? pair : pair.slice(0, separator));
        if (key !== VISITOR_TOKEN_PARAM) {
            kept.push(pair);
            continue;
        }
        found = true;
        if (token === null && separator !== -1) {
            const value = decode(pair.slice(separator + 1));
            if (value && value.length <= MAX_TOKEN_LENGTH && TOKEN_CHARACTERS.test(value)) {
                token = value;
            }
        }
    }
    if (!found) return;

    try {
        const nextSearch = kept.length > 0 ? `?${kept.join("&")}` : "";
        window.history.replaceState(window.history.state, "", `${pathname}${nextSearch}${hash}`);
    } catch {
        // History API unavailable: nothing more we can do about the address bar.
    }
    if (token) pendingToken = token;
}
